// Pure trigger-rule evaluator for agent-skills.
// Editor spec §7 defines the supported operators and coercion rules.

import type { TriggerRule } from "@/types/agent-skills";

export type EvalResult = "match" | "no-match" | "no-context";

/**
 * Evaluate a TriggerRule against a parsed test context object.
 *
 * Returns:
 *  - 'match'      — the rule condition is satisfied.
 *  - 'no-match'   — the rule condition is NOT satisfied.
 *  - 'no-context' — the field is not present on the context object.
 *
 * Coercion rules (per editor spec §7):
 *  - 'gt' and 'lt': both sides are numeric-coerced (Number()).
 *    If either side is NaN after coercion the result is 'no-match'.
 *  - All other ops: context value is stringified via String() before comparison.
 *  - 'regex': rule.value is treated as the pattern string; matched with new RegExp(value).
 */
export function evaluateRule(rule: TriggerRule, context: Record<string, unknown>): EvalResult {
  const rawValue = context[rule.field];

  if (rawValue === undefined) {
    return "no-context";
  }

  const { op, value: ruleValue } = rule;

  switch (op) {
    case "equals": {
      const ctxStr = String(rawValue);
      const ruleStr = String(ruleValue);
      return ctxStr === ruleStr ? "match" : "no-match";
    }

    case "contains": {
      const ctxStr = String(rawValue);
      const ruleStr = String(ruleValue);
      return ctxStr.includes(ruleStr) ? "match" : "no-match";
    }

    case "gt": {
      const ctxNum = Number(rawValue);
      const ruleNum = Number(ruleValue);
      if (isNaN(ctxNum) || isNaN(ruleNum)) return "no-match";
      return ctxNum > ruleNum ? "match" : "no-match";
    }

    case "lt": {
      const ctxNum = Number(rawValue);
      const ruleNum = Number(ruleValue);
      if (isNaN(ctxNum) || isNaN(ruleNum)) return "no-match";
      return ctxNum < ruleNum ? "match" : "no-match";
    }

    case "regex": {
      try {
        const pattern = new RegExp(String(ruleValue));
        return pattern.test(String(rawValue)) ? "match" : "no-match";
      } catch {
        // Invalid regex pattern → treat as no-match
        return "no-match";
      }
    }

    default: {
      // Unknown op — should not happen if rule is validated
      return "no-match";
    }
  }
}
